import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import API from '../../../utils/API';
import commonFunctions from '../../../utils/commonFunctions';

function AssignBuildingsModal({ 
  intl,
  selectedSites,
  selectedCms, 
  dataMap,
  loadTable, 
  setSuccess,
  buildingName, setBuildingName,
  singlewireSites,
  setData,
  generateDataSlice,
  startIndexForTable
}) {

  const [modalError, setModalError] = useState('');
  // Add state for selected site name
  const [selectedSinglewireSiteName, setSelectedSinglewireSiteName] = useState('');
  // Add ref to track if we've initialized for current selection
  const initializedForSites = useRef('');

  // Function to initialize the modal with current data
  const initializeModal = useCallback(() => {
    // Reset values first
    setSelectedSinglewireSiteName('');
    setBuildingName('');
    
    // Only try to pre-populate if we have sites and data
    if (selectedSites.length > 0 && dataMap.data && Object.keys(dataMap.data).length > 0) {
      // Get all assignments
      const assignments = selectedSites.map(siteId => dataMap.data[siteId]?.singlewireSiteName || null);
      const firstAssignment = assignments[0];
      
      // Only pre-populate if ALL sites have the same NON-NULL assignment  
      if (firstAssignment && assignments.every(assignment => assignment === firstAssignment)) {
        setSelectedSinglewireSiteName(firstAssignment);
        setBuildingName(firstAssignment);
      }
    }
  }, [selectedSites, dataMap.data, setBuildingName]);

  // Initialize modal when selectedSites changes AND we haven't initialized for this selection yet
  useEffect(() => {
    const sitesKey = selectedSites.join(',');
    
    // Only initialize if this is a new selection or if we haven't initialized yet
    if (sitesKey !== initializedForSites.current) {
      initializeModal();
      
      // Mark that we've initialized for this selection
      initializedForSites.current = sitesKey;
    }
  }, [selectedSites, initializeModal]);

  // Add event listener for modal show event to ensure fresh initialization
  useEffect(() => {
    const modalElement = document.getElementById('assignBldgModal');
    
    const handleModalShow = () => {
      // Always reinitialize when modal opens to ensure fresh data
      initializeModal();
    };
    
    if (modalElement) {
      modalElement.addEventListener('show.bs.modal', handleModalShow);
      
      return () => {
        modalElement.removeEventListener('show.bs.modal', handleModalShow);
      };
    }
  }, [initializeModal]);

  const handleClose = () => {
    setBuildingName('');
    setSelectedSinglewireSiteName('');
    setModalError('');
    // Reset initialization tracking so modal can reinitialize if reopened
    initializedForSites.current = '';
  };

  const handleBuildingSelect = (e) => {
    const selectedName = e.target.value;
    console.log('Selected Singlewire site name:', selectedName);
    setSelectedSinglewireSiteName(selectedName);
    
    // Also update the display name for consistency
    setBuildingName(selectedName);
  }

  const handleBuildingNameChange = (e) => {
    setBuildingName(e.target.value);
  }

  const handleSubmit = async () => {
    // Use the site name for the API call
    console.log('singlewireSites:', singlewireSites);
    const siteNameToAssign = singlewireSites.length === 0 ? buildingName : selectedSinglewireSiteName;
    
    if(siteNameToAssign === '') {
      setModalError(<FormattedMessage id="BUILDING_NAME_REQUIRED" />);
    } else {
      try {
        console.log('Assigning Singlewire site name:', siteNameToAssign, 'to sites:', selectedSites, 'in CMS:', selectedCms.id);
        await API.assignSinglewireSite(siteNameToAssign, selectedSites, selectedCms.id);
        commonFunctions.closeModal('assignBldgModal');
        setSuccess(<FormattedMessage id="SITE_MAPPING_SUCCESS" />);
        setBuildingName('');
        setSelectedSinglewireSiteName('');
        setModalError('');
        
        // Refresh the table to get the latest data from the server
        // This ensures we have the correct data and triggers WebSocket sync
        loadTable(dataMap, false, selectedCms.id);
        
      } catch (err) {
        setModalError(<FormattedMessage id="SITE_MAPPING_ERROR" />);
      }
    }
  }

  return (
    <div className="modal fade" id="assignBldgModal" tabIndex="-1" aria-labelledby="receiversModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="buildingNameLabel"><FormattedMessage id="ASSIGN_BUILDING" /></h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={handleClose}></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              {singlewireSites.length === 0 ? (
                <div>
                  <div className='row'>
                    <div className='firstNameSection mb-2'>
                      <label htmlFor='firstNameLabel' className='col-form-label'>
                        <FormattedMessage id='BUILDING_NAME' />
                      </label>
                      <input
                        id="buildingNameInput"
                        onChange={handleBuildingNameChange}
                        value={buildingName}
                        placeholder={intl.formatMessage({ id: 'ENTER_BUILDING_NAME' })}
                        name='buildingName'
                        className={`form-control`}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <p className="text-center text-muted align-middle mb-0"><small><FormattedMessage id="SINGLEWIRE_SITE_NAME_DESC" /></small></p>
                  </div>
                </div>
              ) : (
              <>
                <div className='row'>
                  <div className='firstNameSection mb-2'>
                    <label htmlFor='buildingSelect' className='col-form-label'>
                      <FormattedMessage id='SITE_NAME' />
                    </label>
                    <select
                      id="buildingSelect"
                      onChange={handleBuildingSelect}
                      value={selectedSinglewireSiteName}
                      className="form-select"
                    >
                      <option value="">
                        {intl.formatMessage({ id: 'SELECT_SITE' })}
                      </option>
                      {singlewireSites.map((site) => (
                        <option key={site.id} value={site.name}>
                          {site.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="row">
                  <p className="text-center text-muted align-middle mb-0">
                    <small>
                      <FormattedMessage id="SINGLEWIRE_SITE_SELECT_DESC" />
                    </small>
                  </p>
                </div>
              </>
              )}
            </form>
            {modalError && (
              <div className="text-danger" role="alert">
                {modalError}
              </div>
            )}
          </div>
          <div className="modal-footer justify-content-between">
            <button id="closeBldgBtn" type="button" className="btn back-btn" data-bs-dismiss="modal" onClick={handleClose}>
              <FormattedMessage id="CLOSE" />
            </button>
            <button id="saveBldgBtn" className='btn save-btn' type='button' onClick={handleSubmit}>
              <FormattedMessage id='SAVE' />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default injectIntl(AssignBuildingsModal);

